import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { ROUTES, buildRoute } from '@/constants/routes';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import { useFraudReports, useResolveReport } from '../hooks/useFraud';
import { ReportSeverityBadge, ReportTypeBadge } from '../components/ReportSeverityBadge';
import type { ReportSeverity, ReportType } from '../api/fraud.api';

const severityOptions = [
  { value: 'all', label: 'All Severities' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const typeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'driver_report', label: 'Driver Report' },
  { value: 'auto_cancellation_rate', label: 'High Cancellation Rate' },
  { value: 'auto_rapid_booking', label: 'Rapid Booking Pattern' },
  { value: 'auto_post_accept_cancel', label: 'Post-Accept Cancel' },
];

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'false', label: 'Unresolved' },
  { value: 'true', label: 'Resolved' },
];

export function FraudReportsPage() {
  const navigate = useNavigate();
  const [severity, setSeverity] = useState<ReportSeverity | 'all'>('all');
  const [reportType, setReportType] = useState<ReportType | 'all'>('all');
  const [resolved, setResolved] = useState<'all' | 'true' | 'false'>('false');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useFraudReports({
    page,
    limit: 20,
    severity: severity === 'all' ? undefined : severity,
    report_type: reportType === 'all' ? undefined : reportType,
    is_resolved: resolved === 'all' ? undefined : resolved === 'true',
  });

  const { mutate: resolve, isPending: isResolving } = useResolveReport();

  const reports = data?.reports ?? [];
  const totalPages = data?.pages ?? 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fraud Reports"
        description="Monitor suspicious activity and auto-detected fraud signals"
        breadcrumbs={[{ label: 'Fraud & Safety', href: ROUTES.FRAUD }, { label: 'Reports' }]}
        actions={
          <Button variant="outline" onClick={() => navigate(ROUTES.UNBLOCK_REQUESTS)}>
            Unblock Requests
            {data?.total ? (
              <Badge variant="destructive" className="ml-2 h-5 min-w-5 flex items-center justify-center p-0 text-xs">
                {data.total}
              </Badge>
            ) : null}
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Select value={resolved} onChange={(v) => { setResolved(v as any); setPage(1); }} options={statusOptions} className="w-36" />
            <Select value={severity} onChange={(v) => { setSeverity(v as any); setPage(1); }} options={severityOptions} className="w-40" />
            <Select value={reportType} onChange={(v) => { setReportType(v as any); setPage(1); }} options={typeOptions} className="w-52" />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : reports.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon="default"
                title="No fraud reports"
                description="No reports match the selected filters."
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reported</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <button
                        className="text-sm font-medium hover:underline text-left"
                        onClick={() => navigate(buildRoute(ROUTES.CUSTOMER_DETAIL, { id: report.customer_id }))}
                      >
                        {report.customer?.name ?? report.customer_id.slice(0, 8)}
                      </button>
                      {report.customer?.phone && (
                        <p className="text-xs text-muted-foreground">{report.customer.phone}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <ReportTypeBadge type={report.report_type} />
                    </TableCell>
                    <TableCell>
                      <ReportSeverityBadge severity={report.severity} />
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <p className="text-sm truncate">{report.reason}</p>
                    </TableCell>
                    <TableCell>
                      {report.is_resolved ? (
                        <Badge variant="success">Resolved</Badge>
                      ) : (
                        <Badge variant="warning">Open</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(report.created_at)}
                    </TableCell>
                    <TableCell>
                      {!report.is_resolved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolve(report.id)}
                          disabled={isResolving}
                        >
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          Resolve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
